import React, { useState } from 'react';
import OfferAiSummaryBoundary from './OfferAiSummaryBoundary';

// Per-offer-card button. Five possible states:
//   (none record)    → "Generate summary" (user-initiated — primary party only)
//   PENDING          → "Generating…" (disabled pill)
//   READY            → "View summary"
//   FAILED           → "Regenerate"
//   loadError        → subtle "↻ Retry summary" text link (primary party only)
//
// Copy avoids "AI" per the feature's user-facing copy rule.
const ButtonInner = ({ offerAi, offerId, className = 'oas-btn' }) => {
  const [generating, setGenerating] = useState(false);

  if (!offerAi) return null;

  // Error state renders as a subtle text link rather than a pill — the retry
  // UX is a nice-to-have for transient failures and should never compete with
  // the primary action buttons in the row.
  if (offerAi.loadError) {
    if (!offerAi.viewerIsPrimary) return null;
    return (
      <button
        type="button"
        className="oas-retry-link"
        title="Couldn't load summaries. Click to retry."
        onClick={(e) => {
          e.stopPropagation();
          offerAi.refresh();
        }}
      >
        ↻ Retry summary
      </button>
    );
  }

  if (!offerAi.enabled) return null;

  const status = offerAi.summaryForOffer(offerId);

  // No record yet → let a primary party kick off the first generation.
  // On success, open the progress modal immediately so the user sees the
  // "Reading the offer and generating your summary…" state live rather than
  // staring at a disabled "Generating…" pill with no feedback.
  if (!status) {
    if (!offerAi.viewerIsPrimary || !offerId) return null;
    return (
      <button
        type="button"
        className={`${className} oas-btn-generate`}
        disabled={generating}
        onClick={async (e) => {
          e.stopPropagation();
          if (generating) return;
          setGenerating(true);
          try {
            const summaryId = await offerAi.generate(offerId);
            if (summaryId) offerAi.open(summaryId);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[OfferAiSummary] generate failed:', err);
          } finally {
            setGenerating(false);
          }
        }}
      >
        {generating ? 'Generating…' : 'Generate summary'}
      </button>
    );
  }

  const serverStatus = status.status;
  const isPending = serverStatus === 'PENDING';
  const isReady = serverStatus === 'READY';
  const isFailed = serverStatus === 'FAILED';

  let label = 'Generating…';
  let variant = 'oas-btn-generating';
  if (isReady) {
    label = 'View summary';
    variant = 'oas-btn-view';
  } else if (isFailed) {
    label = 'Regenerate';
    variant = 'oas-btn-regenerate';
  }

  const onClick = async (e) => {
    e.stopPropagation();
    // FAILED → actually fire the regenerate POST, THEN open the modal so the
    // user sees the Generating… state. Previously this just opened the modal,
    // which left the Regenerate button looking like a dead no-op.
    if (isFailed && offerAi.viewerIsPrimary) {
      if (generating) return;
      setGenerating(true);
      try {
        await offerAi.regenerate(status.id);
        offerAi.open(status.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[OfferAiSummary] regenerate failed:', err);
        offerAi.open(status.id);
      } finally {
        setGenerating(false);
      }
      return;
    }
    // PENDING / READY → open the modal.
    offerAi.open(status.id);
  };

  return (
    <button
      type="button"
      className={`${className} ${variant}`}
      disabled={isFailed && generating}
      onClick={onClick}
    >
      {isFailed && generating ? 'Starting…' : label}
    </button>
  );
};

const OfferSummaryButton = (props) => (
  <OfferAiSummaryBoundary>
    <ButtonInner {...props} />
  </OfferAiSummaryBoundary>
);

export default OfferSummaryButton;
